// Dessin du sol (voir core/scenery.js#generateGround) : pas d'objets, une texture de traînées et un
// relief de bosses en fausse perspective. Plus une rangée est basse, plus elle est proche, large et
// espacée, et plus elle défile vite quand on tourne l'objectif (parallax par bande de profondeur).
// L'astre du ciel (lune ou soleil) éclaire le sol : une nappe de lumière s'étale sous lui, les
// traînées proches de sa verticale s'éclaircissent, et chaque bosse a un versant clair tourné vers
// lui et une ombre portée de l'autre côté. Selon le thème : des crabes sur la plage lisse, des champignons et des mousses en forêt, des pierres, des edelweiss et des cairns en montagne, des racines et des lianes en jungle, des tombes, des croix et des os au cimetière. Couleurs : palette `ground` du décor (config/theme.js).
import { VIEW_SIZE } from '../config/constants.js';
import { view } from './viewState.js';
import { wrapX } from '../core/camera.js';
import { FAR_PARALLAX, GROUND_BANDS, groundScale } from '../core/scenery.js';
import { TAU, oval, limb, disc, arch } from './paintKit.js';

const TEXTURE_ROWS = 24;
// Force de la lumière selon l'astre (le soleil rase l'horizon, la lune est plus douce).
const LIGHT = { sun: 1, full: 0.75, crescent: 0.55 };

const hash = (a, b) => {
  const v = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return v - Math.floor(v);
};
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const bandAt = (frac) => GROUND_BANDS.find((b) => frac <= b.to) ?? GROUND_BANDS.at(-1);

/** Traînées fines du sol ; celles proches de la verticale de l'astre (`lightX`, px vue) sont plus claires. */
function drawTexture(ctx, ground, camera, k, colors, lightX, light) {
  const half = ground.worldSize / 2;
  const dark = new Path2D();
  const lit = new Path2D();
  for (let r = 0; r < TEXTURE_ROWS; r++) {
    const frac = 0.025 * 1.17 ** r;
    if (frac > 1) break;
    const scale = groundScale(frac);
    const thick = 1.6 * scale;
    const vy = frac * half - camera.y + VIEW_SIZE / 2;
    if (vy < -thick || vy > VIEW_SIZE + thick) continue;
    const band = bandAt(frac);
    const period = ground.worldSize * band.parallax;
    const shift = camera.x * band.parallax;
    const count = Math.max(3, Math.round(period / (100 * scale)));
    const cell = period / count;
    for (let n = 0; n < count; n++) {
      if (hash(r * 7.31 + ground.seed, n * 1.77) < 0.5) continue;
      const len = cell * (0.25 + 0.4 * hash(n * 3.1, r * 5.3 + ground.seed));
      const vx = wrapX(-period / 2 + (n + 0.5) * cell - shift, period) + view.width / 2;
      if (vx + len / 2 < 0 || vx - len / 2 > view.width) continue;
      const near = light > 0 && Math.abs(vx - lightX) < VIEW_SIZE * 0.3;
      oval(near ? lit : dark, vx * k, vy * k, (len / 2) * k, Math.max(0.5, (thick / 2) * k));
    }
  }
  ctx.globalAlpha = 0.55;
  ctx.fillStyle = colors.main;
  ctx.fill(dark);
  ctx.globalAlpha = 0.35 + 0.4 * light;
  ctx.fillStyle = colors.accent;
  ctx.fill(lit);
  ctx.globalAlpha = 1;
}

/** Nappe de lumière de l'astre sur le sol : large sous lui, qui s'éteint avec la distance et vers les pieds. */
function drawPool(ctx, body, pal, horizonPx, lightX, k, light) {
  const radius = VIEW_SIZE * 1.0 * k;
  const g = ctx.createRadialGradient(lightX * k, horizonPx, 0, lightX * k, horizonPx, radius);
  g.addColorStop(0, pal.body);
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, horizonPx, ctx.canvas.width, ctx.canvas.height - horizonPx);
  ctx.clip();
  ctx.globalAlpha = 0.7 * light;
  ctx.fillStyle = g;
  ctx.fillRect(0, horizonPx, ctx.canvas.width, ctx.canvas.height - horizonPx);
  ctx.restore();
}

/** Une bosse : ombre portée au sol côté opposé à l'astre, corps sombre, versant éclairé côté astre. */
function drawMound(ctx, m, vx, vy, k, colors, lightX, light) {
  const dir = clamp((lightX - vx) / (VIEW_SIZE * 0.6), -1, 1);
  const rx = (m.w / 2) * k;
  const ry = m.h * k;
  const cx = vx * k;
  const cy = vy * k;
  ctx.fillStyle = colors.deep;
  ctx.globalAlpha = 0.28;
  ctx.beginPath();
  ctx.ellipse(cx - dir * rx * 0.45, cy + ry * 0.35, rx * 1.05, ry * 0.45, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.clip();
  // Corps sombre, puis des plages de plus en plus petites et claires vers le versant tourné vers l'astre
  ctx.globalAlpha = 0.3;
  ctx.fillStyle = colors.hollow;
  ctx.fillRect(cx - rx, cy - ry, rx * 2, ry * 2);
  ctx.fillStyle = colors.accent;
  for (let i = 0; i < 4; i++) {
    const f = 1 - i * 0.2;
    ctx.globalAlpha = (0.06 + 0.1 * Math.abs(dir)) * light;
    ctx.beginPath();
    ctx.ellipse(cx + dir * rx * (0.12 + i * 0.12), cy - ry * (0.15 + i * 0.1), rx * 0.8 * f, ry * 1.1 * f, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
  ctx.globalAlpha = 1;
}

/** Crabe vu de dessus-trois-quarts, qui trottine de côté : carapace, deux pinces, pattes qui battent, yeux sur tiges. */
function drawCrab(ctx, c, vx, vy, k, colors, t) {
  const w = c.w * k;
  const dir = c.flip ? -1 : 1;
  const run = Math.sin(t * 0.7 + c.phase); // va-et-vient lent, de côté
  const x = vx * k + run * c.w * 0.9 * k;
  const y = vy * k;
  const step = t * 9 + c.phase;
  const body = new Path2D();
  const legs = new Path2D();
  const shadow = new Path2D();
  const dark = new Path2D();
  oval(shadow, x, y + w * 0.1, w * 0.52, w * 0.12);
  for (const side of [-1, 1]) {
    for (let i = 0; i < 3; i++) {
      const wig = Math.sin(step + i * 1.3 + side) * w * 0.04;
      const ox = x + side * w * (0.22 + i * 0.05);
      limb(legs, [ox, y - w * 0.04], [x + side * w * (0.46 + i * 0.03), y + w * (0.02 + i * 0.07) + wig], w * 0.05, w * 0.03);
    }
    // pince, bras tendu vers l'avant ; la pince « bat » à contre-temps de l'autre
    const open = 0.1 + 0.05 * Math.sin(step * 0.5 + side * 1.6);
    const px = x + side * w * 0.4;
    const py = y - w * 0.3;
    limb(legs, [x + side * w * 0.22, y - w * 0.1], [px, py + w * 0.05], w * 0.06, w * 0.05);
    oval(body, px, py, w * 0.13, w * (0.1 + open * 0.2), side * 0.5);
    oval(dark, px + side * w * 0.02, py - w * 0.04, w * 0.04, w * 0.04); // creux de la pince
    // yeux sur tiges
    limb(legs, [x + side * w * 0.07, y - w * 0.12], [x + side * w * 0.09, y - w * 0.24], w * 0.03);
    disc(body, x + side * w * 0.09, y - w * 0.26, w * 0.045);
  }
  oval(body, x, y - w * 0.02, w * 0.3, w * 0.2); // carapace
  oval(dark, x, y + w * 0.02, w * 0.2, w * 0.05); // ligne de la bouche
  ctx.globalAlpha = 0.3;
  ctx.fillStyle = colors.deep;
  ctx.fill(shadow);
  ctx.globalAlpha = 1;
  ctx.fillStyle = colors.lava;
  ctx.fill(legs);
  ctx.fill(body);
  ctx.globalAlpha = 0.5;
  ctx.fillStyle = colors.hollow;
  ctx.fill(dark);
  ctx.globalAlpha = 1;
  // reflet clair sur la carapace
  ctx.fillStyle = colors.accent;
  const hi = new Path2D();
  oval(hi, x + dir * w * 0.06, y - w * 0.09, w * 0.12, w * 0.05);
  ctx.globalAlpha = 0.45;
  ctx.fill(hi);
  ctx.globalAlpha = 1;
}

/**
 * Grosse araignée de la jungle, vue de dessus-trois-quarts, qui patrouille d'un côté à l'autre du sol : abdomen et
 * céphalothorax sombres, huit pattes articulées qui trottinent en alternance, plusieurs yeux qui luisent.
 */
function drawSpider(ctx, c, vx, vy, k, colors, t) {
  const w = c.w * k;
  const phase = t * 0.55 + c.phase;
  const heading = Math.cos(phase) >= 0 ? 1 : -1; // elle regarde là où elle va
  const x = vx * k + Math.sin(phase) * c.w * 1.1 * k;
  const y = vy * k;
  const step = t * 11 + c.phase;
  const legs = new Path2D();
  const body = new Path2D();
  const shadow = new Path2D();
  const eyes = new Path2D();
  oval(shadow, x, y + w * 0.12, w * 0.55, w * 0.13);
  // Huit pattes : quatre de chaque côté (haut/bas de l'écran), deux segments chacune, qui battent à contre-temps
  for (const side of [-1, 1]) {
    for (let i = 0; i < 4; i++) {
      const ox = x + heading * w * (0.2 - i * 0.1);
      const oy = y - w * 0.1;
      const wig = Math.sin(step + i * 1.6 + (side > 0 ? Math.PI : 0)) * w * 0.05;
      const kneeX = ox + heading * w * (0.14 - i * 0.07) + wig * 0.5;
      const kneeY = oy + side * w * 0.3;
      const footX = kneeX + heading * w * (0.08 - i * 0.07) + wig;
      const footY = oy + side * w * (0.46 + i * 0.015);
      limb(legs, [ox, oy], [kneeX, kneeY - side * w * 0.04], w * 0.045, w * 0.032);
      limb(legs, [kneeX, kneeY - side * w * 0.04], [footX, footY], w * 0.032, w * 0.018);
    }
  }
  oval(body, x - heading * w * 0.2, y - w * 0.1, w * 0.26, w * 0.19); // abdomen
  oval(body, x + heading * w * 0.14, y - w * 0.1, w * 0.15, w * 0.12); // céphalothorax
  for (const o of [-1, 1]) {
    disc(eyes, x + heading * w * 0.25, y - w * (0.1 - o * 0.03), w * 0.022);
    disc(eyes, x + heading * w * 0.2, y - w * (0.1 - o * 0.06), w * 0.016);
  }
  ctx.globalAlpha = 0.3;
  ctx.fillStyle = colors.deep;
  ctx.fill(shadow);
  ctx.globalAlpha = 1;
  ctx.fillStyle = colors.hollow;
  ctx.fill(legs);
  ctx.fillStyle = colors.hollow;
  ctx.fill(body);
  // Reflet sur l'abdomen et sur les pattes, et marque claire sur le dos
  ctx.fillStyle = colors.accent;
  const sheen = new Path2D();
  oval(sheen, x - heading * w * 0.22, y - w * 0.17, w * 0.12, w * 0.045);
  oval(sheen, x - heading * w * 0.2, y - w * 0.1, w * 0.04, w * 0.1);
  ctx.globalAlpha = 0.4;
  ctx.fill(sheen);
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = colors.glow ?? colors.accent;
  ctx.fill(eyes);
  ctx.globalAlpha = 1;
}

/** Mousse : un tapis de touffes vertes à plat sur le sol, plus claires du côté de l'astre. */
function drawMoss(ctx, c, vx, vy, k, colors, t, lightX, light) {
  const dir = clamp((lightX - vx) / (VIEW_SIZE * 0.6), -1, 1);
  const w = c.w * k;
  const h = w * 0.16; // étalement en profondeur, déjà aplati par la perspective
  const base = new Path2D();
  const tufts = new Path2D();
  const tips = new Path2D();
  for (let i = 0; i < 16; i++) { // touffes arrondies, serrées, de tailles inégales : un coussin, pas des nénuphars
    const ang = hash(c.phase, i * 1.7) * Math.PI * 2;
    const rad = Math.sqrt(hash(i * 2.3, c.phase));
    const x = vx * k + Math.cos(ang) * rad * w * 0.45;
    const y = vy * k + Math.sin(ang) * rad * h * 0.5;
    const r = w * (0.035 + 0.05 * hash(i, c.phase * 3));
    oval(base, x, y + r * 0.25, r * 1.15, r * 0.7);
    oval(tufts, x, y - r * 0.1, r, r * 0.75);
    oval(tips, x + dir * r * 0.3, y - r * 0.4, r * 0.55, r * 0.35);
  }
  ctx.fillStyle = colors.hollow;
  ctx.globalAlpha = 0.5;
  ctx.fill(base);
  ctx.fillStyle = colors.moss ?? colors.main;
  ctx.globalAlpha = 0.9;
  ctx.fill(tufts);
  ctx.fillStyle = colors.accent;
  ctx.globalAlpha = 0.25 + 0.3 * light;
  ctx.fill(tips);
  ctx.globalAlpha = 1;
}

/** Champignons phosphorescents : un petit groupe de chapeaux lumineux qui pulsent doucement. */
function drawMushrooms(ctx, c, vx, vy, k, colors, t) {
  const w = c.w * k;
  const pulse = 0.75 + 0.25 * Math.sin(t * 1.6 + c.phase);
  const stems = new Path2D();
  const caps = new Path2D();
  const spots = new Path2D();
  const halo = new Path2D();
  const shadow = new Path2D();
  const n = 2 + Math.floor(hash(c.phase, 4) * 2);
  for (let i = 0; i < n; i++) {
    const size = w * (i === 0 ? 0.5 : 0.28 + 0.12 * hash(i, c.phase));
    const x = vx * k + (i === 0 ? 0 : (i % 2 ? -1 : 1) * w * (0.35 + 0.1 * i));
    const y = vy * k + (i === 0 ? 0 : w * 0.06 * (i % 2 ? 1 : -1));
    oval(shadow, x, y + size * 0.05, size * 0.7, size * 0.14);
    limb(stems, [x, y], [x + (i % 2 ? -1 : 1) * size * 0.05, y - size * 0.7], size * 0.26, size * 0.2);
    const capY = y - size * 0.7;
    caps.moveTo(x - size * 0.62, capY + size * 0.04);
    caps.ellipse(x, capY + size * 0.04, size * 0.62, size * 0.5, 0, Math.PI, 0);
    caps.closePath();
    oval(caps, x, capY + size * 0.06, size * 0.62, size * 0.1); // dessous du chapeau
    disc(spots, x - size * 0.2, capY - size * 0.18, size * 0.07);
    disc(spots, x + size * 0.18, capY - size * 0.26, size * 0.05);
    disc(halo, x, capY, size * 1.1);
  }
  ctx.fillStyle = colors.deep;
  ctx.globalAlpha = 0.3;
  ctx.fill(shadow);
  ctx.fillStyle = colors.glow;
  ctx.globalAlpha = 0.14 * pulse;
  ctx.fill(halo);
  ctx.globalAlpha = 1;
  ctx.fillStyle = colors.accent;
  ctx.fill(stems);
  ctx.fillStyle = colors.glow;
  ctx.globalAlpha = 0.65 + 0.35 * pulse;
  ctx.fill(caps);
  ctx.globalAlpha = 0.5;
  ctx.fillStyle = colors.hollow;
  ctx.fill(spots);
  ctx.globalAlpha = 1;
}

/** Une pierre : corps sombre, arête claire côté astre, ombre portée du côté opposé. */
function stone(ctx, x, y, rx, ry, dir, colors, light) {
  const shadow = new Path2D();
  const body = new Path2D();
  const top = new Path2D();
  oval(shadow, x - dir * rx * 0.3, y + ry * 0.3, rx * 1.1, ry * 0.45);
  oval(body, x, y - ry * 0.4, rx, ry);
  oval(top, x + dir * rx * 0.25, y - ry * 0.75, rx * 0.6, ry * 0.5);
  ctx.fillStyle = colors.deep;
  ctx.globalAlpha = 0.35;
  ctx.fill(shadow);
  ctx.globalAlpha = 1;
  ctx.fillStyle = colors.main;
  ctx.fill(body);
  ctx.fillStyle = colors.accent;
  ctx.globalAlpha = 0.25 + 0.4 * light;
  ctx.fill(top);
  ctx.globalAlpha = 1;
}

function drawStones(ctx, c, vx, vy, k, colors, t, lightX, light) {
  const dir = clamp((lightX - vx) / (VIEW_SIZE * 0.6), -1, 1);
  const w = c.w * k;
  const n = 1 + Math.floor(hash(c.phase, 2) * 3);
  for (let i = 0; i < n; i++) {
    const size = w * (i === 0 ? 0.42 : 0.2 + 0.1 * hash(i, c.phase));
    const x = vx * k + (i === 0 ? 0 : (i % 2 ? -1 : 1) * w * (0.5 + 0.1 * i));
    stone(ctx, x, vy * k + (i % 2) * w * 0.03, size, size * (0.5 + 0.2 * hash(c.phase, i)), dir, colors, light);
  }
}

/** Cairn : trois ou quatre galets empilés, du plus large au plus petit. */
function drawCairn(ctx, c, vx, vy, k, colors, t, lightX, light) {
  const dir = clamp((lightX - vx) / (VIEW_SIZE * 0.6), -1, 1);
  const w = c.w * k;
  const n = 3 + Math.floor(hash(c.phase, 6) * 2);
  let y = vy * k;
  for (let i = 0; i < n; i++) {
    const rx = w * (0.5 - i * 0.1) * (0.9 + 0.2 * hash(i, c.phase));
    const ry = w * 0.2;
    stone(ctx, vx * k + (hash(c.phase, i) - 0.5) * w * 0.08, y, rx, ry, dir, colors, light);
    y -= ry * 1.35;
  }
}

/** Edelweiss : deux ou trois fleurs de feutre blanc, étoilées, au cœur jaune, qui ondulent à peine dans le vent. */
function drawEdelweiss(ctx, c, vx, vy, k, colors, t) {
  const w = c.w * k;
  const stems = new Path2D();
  const petals = new Path2D();
  const hearts = new Path2D();
  const n = 2 + Math.floor(hash(c.phase, 8) * 2);
  for (let i = 0; i < n; i++) {
    const size = w * (i === 0 ? 0.5 : 0.32 + 0.1 * hash(i, c.phase));
    const x = vx * k + (i === 0 ? 0 : (i % 2 ? -1 : 1) * w * (0.35 + 0.12 * i));
    const sway = Math.sin(t * 1.3 + c.phase + i) * size * 0.08;
    const hx = x + sway;
    const hy = vy * k - size * 1.1;
    limb(stems, [x, vy * k], [hx, hy + size * 0.1], size * 0.1, size * 0.08);
    limb(stems, [x, vy * k - size * 0.4], [x + size * 0.28, vy * k - size * 0.6], size * 0.06, size * 0.04); // feuille
    for (let j = 0; j < 8; j++) { // pétales : bractées allongées en étoile, vues un peu de biais
      const a = (j / 8) * TAU + 0.2;
      oval(petals, hx + Math.cos(a) * size * 0.2, hy + Math.sin(a) * size * 0.12, size * 0.22, size * 0.06, a * 0.6);
    }
    disc(hearts, hx, hy, size * 0.09);
  }
  ctx.fillStyle = colors.main;
  ctx.fill(stems);
  ctx.fillStyle = colors.petal ?? colors.accent;
  ctx.fill(petals);
  ctx.fillStyle = colors.glow;
  ctx.fill(hearts);
}

/** Trace une courbe épaisse segment par segment (largeur `widths[i]` au point i, bouts arrondis). */
function strokeCurve(ctx, pts, widths, dy = 0) {
  ctx.lineCap = 'round';
  for (let i = 1; i < pts.length; i++) {
    ctx.lineWidth = widths[i];
    ctx.beginPath();
    ctx.moveTo(pts[i - 1][0], pts[i - 1][1] + dy);
    ctx.lineTo(pts[i][0], pts[i][1] + dy);
    ctx.stroke();
  }
}

/** Points d'une courbe sinueuse couchée sur le sol, centrée en (cx, cy), de largeur `w`. */
function windingPoints(cx, cy, w, c, n, amp) {
  const sign = c.flip ? -1 : 1;
  return Array.from({ length: n + 1 }, (_, i) => [
    cx + sign * (i / n - 0.5) * w,
    cy + Math.sin(i * 0.9 + c.phase) * w * amp + Math.sin(i * 0.37 + c.phase * 2) * w * amp * 0.6,
  ]);
}

/** Racine qui court au sol : large au pied de l'arbre, effilée au bout, avec une radicelle et un reflet côté astre. */
function drawRoot(ctx, c, vx, vy, k, colors, t, lightX, light) {
  const w = c.w * k;
  const n = 14;
  const pts = windingPoints(vx * k, vy * k, w, c, n, 0.035);
  const widths = pts.map((_, i) => w * 0.11 * (1 - (i / n) * 0.8));
  const branchAt = 5;
  const sign = c.flip ? -1 : 1;
  const [bx, by] = pts[branchAt];
  const branch = [[bx, by], [bx + sign * w * 0.08, by + w * 0.04], [bx + sign * w * 0.15, by + w * 0.05], [bx + sign * w * 0.22, by + w * 0.09]];
  const bw = [0, w * 0.035, w * 0.026, w * 0.015];
  ctx.strokeStyle = colors.deep;
  ctx.globalAlpha = 0.35;
  strokeCurve(ctx, pts, widths, w * 0.035);
  ctx.globalAlpha = 1;
  ctx.strokeStyle = colors.root ?? colors.main;
  strokeCurve(ctx, pts, widths);
  strokeCurve(ctx, branch, bw);
  ctx.strokeStyle = colors.accent; // reflet sur le dessus
  ctx.globalAlpha = 0.2 + 0.35 * light;
  strokeCurve(ctx, pts, widths.map((v) => v * 0.28), -w * 0.018);
  ctx.globalAlpha = 1;
}

/** Liane au sol : un fil sinueux garni de feuilles de chaque côté et de quelques baies lumineuses. */
function drawVine(ctx, c, vx, vy, k, colors, t) {
  const w = c.w * k;
  const n = 16;
  const pts = windingPoints(vx * k, vy * k, w, c, n, 0.05);
  const leaves = new Path2D();
  const berries = new Path2D();
  const sway = (i) => Math.sin(t * 1.1 + c.phase + i) * w * 0.004;
  pts.forEach(([x, y], i) => {
    if (i % 2 || i === 0) return;
    const side = i % 4 ? -1 : 1;
    oval(leaves, x + side * w * 0.025, y + side * w * 0.03 + sway(i), w * 0.05, w * 0.02, side * 0.6);
    if (i % 6 === 0) disc(berries, x, y - w * 0.02, w * 0.012);
  });
  ctx.strokeStyle = colors.deep;
  ctx.globalAlpha = 0.3;
  strokeCurve(ctx, pts, pts.map(() => w * 0.02), w * 0.025);
  ctx.globalAlpha = 1;
  ctx.strokeStyle = colors.main;
  strokeCurve(ctx, pts, pts.map(() => w * 0.02));
  ctx.fillStyle = colors.leaf ?? colors.accent;
  ctx.fill(leaves);
  ctx.fillStyle = colors.glow;
  ctx.fill(berries);
}

/** Pierre tombale : stèle arrondie sur socle, bord éclairé côté astre, croix gravée, lueur verte au pied. */
function drawTomb(ctx, c, vx, vy, k, colors, t, lightX, light) {
  const dir = clamp((lightX - vx) / (VIEW_SIZE * 0.6), -1, 1);
  const w = c.w * k;
  const sw = w * 0.5;
  const sh = w * 0.8;
  const x = vx * k - sw / 2;
  const base = vy * k;
  const shadow = new Path2D();
  const glow = new Path2D();
  const slab = new Path2D();
  const plinth = new Path2D();
  const cross = new Path2D();
  oval(shadow, vx * k - dir * sw * 0.5, base + sw * 0.05, sw * 0.85, sw * 0.14);
  oval(glow, vx * k, base, sw * 0.9, sw * 0.16);
  plinth.rect(x - sw * 0.1, base - sh * 0.1, sw * 1.2, sh * 0.1);
  arch(slab, x, base - sh, sw, sh * 0.92, sw / 2);
  cross.rect(vx * k - sw * 0.04, base - sh * 0.72, sw * 0.08, sh * 0.4);
  cross.rect(vx * k - sw * 0.16, base - sh * 0.62, sw * 0.32, sw * 0.08);
  ctx.fillStyle = colors.deep;
  ctx.globalAlpha = 0.35;
  ctx.fill(shadow);
  ctx.fillStyle = colors.glow;
  ctx.globalAlpha = 0.1 + 0.05 * Math.sin(t * 1.4 + c.phase);
  ctx.fill(glow);
  ctx.globalAlpha = 1;
  ctx.fillStyle = colors.main;
  ctx.fill(plinth);
  ctx.fillStyle = colors.stone ?? colors.main;
  ctx.fill(slab);
  ctx.save();
  ctx.clip(slab);
  ctx.fillStyle = colors.accent; // tranche éclairée côté astre
  ctx.globalAlpha = 0.2 + 0.35 * light;
  ctx.fillRect(dir >= 0 ? x + sw * 0.78 : x, base - sh, sw * 0.22, sh);
  ctx.restore();
  ctx.fillStyle = colors.hollow;
  ctx.globalAlpha = 0.65;
  ctx.fill(cross);
  ctx.globalAlpha = 1;
}

/** Croix de bois ou de pierre plantée de travers dans un petit tertre. */
function drawCross(ctx, c, vx, vy, k, colors, t, lightX, light) {
  const dir = clamp((lightX - vx) / (VIEW_SIZE * 0.6), -1, 1);
  const w = c.w * k;
  const n = 1 + (hash(c.phase, 9) > 0.55 ? 1 : 0);
  for (let i = 0; i < n; i++) {
    const s = w * (i === 0 ? 1 : 0.7);
    const x = vx * k + (i === 0 ? 0 : (c.flip ? -1 : 1) * w * 0.8);
    const tilt = (hash(c.phase, i * 3.1) - 0.5) * 0.3;
    const mound = new Path2D();
    const shadow = new Path2D();
    oval(shadow, x - dir * s * 0.3, vy * k + s * 0.04, s * 0.45, s * 0.07);
    oval(mound, x, vy * k, s * 0.3, s * 0.07);
    ctx.fillStyle = colors.deep;
    ctx.globalAlpha = 0.35;
    ctx.fill(shadow);
    ctx.globalAlpha = 1;
    ctx.fillStyle = colors.main;
    ctx.fill(mound);
    ctx.save();
    ctx.translate(x, vy * k);
    ctx.rotate(tilt);
    const wood = new Path2D();
    wood.rect(-s * 0.05, -s * 1.1, s * 0.1, s * 1.1);
    wood.rect(-s * 0.28, -s * 0.85, s * 0.56, s * 0.1);
    ctx.fillStyle = colors.stone ?? colors.main;
    ctx.fill(wood);
    ctx.fillStyle = colors.accent;
    ctx.globalAlpha = 0.2 + 0.35 * light;
    ctx.fillRect(dir >= 0 ? s * 0.01 : -s * 0.05, -s * 1.1, s * 0.04, s * 1.1);
    ctx.restore();
    ctx.globalAlpha = 1;
  }
}

/** Un os long (fémur) abandonné sur le sol : diaphyse et deux épiphyses à chaque bout. */
function drawBone(ctx, c, vx, vy, k, colors) {
  const w = c.w * k;
  const a = (hash(c.phase, 5) - 0.5) * 0.5;
  const dx = Math.cos(a) * w * 0.5;
  const dy = Math.sin(a) * w * 0.5 * 0.5;
  const x = vx * k;
  const y = vy * k;
  const bone = new Path2D();
  const shadow = new Path2D();
  limb(shadow, [x - dx, y - dy + w * 0.04], [x + dx, y + dy + w * 0.04], w * 0.1);
  limb(bone, [x - dx, y - dy], [x + dx, y + dy], w * 0.09);
  for (const s of [-1, 1]) {
    for (const o of [-1, 1]) disc(bone, x + s * dx + o * w * 0.012, y + s * dy + o * w * 0.045, w * 0.065);
  }
  ctx.fillStyle = colors.deep;
  ctx.globalAlpha = 0.35;
  ctx.fill(shadow);
  ctx.globalAlpha = 1;
  ctx.fillStyle = colors.accent;
  ctx.fill(bone);
}

const PROP_DRAWERS = {
  crab: drawCrab, moss: drawMoss, mushroom: drawMushrooms, stone: drawStones, cairn: drawCairn, edelweiss: drawEdelweiss,
  root: drawRoot, vine: drawVine, spider: drawSpider, tomb: drawTomb, cross: drawCross, bone: drawBone,
};

export function createGroundPainter(ctx) {
  /**
   * Peint le sol : texture, nappe de lumière de l'astre, puis les bosses du plus lointain au plus proche.
   * @param {import('../core/scenery.js').Scenery['ground']} ground
   * @param {import('../core/scenery.js').SkyBody} body  astre du ciel (peut manquer)
   * @param {object} pal  palette du décor : pal.ground (config/theme.js)
   */
  function paint(ground, body, camera, k, pal, t = 0) {
    const colors = pal.ground;
    if (!ground || !colors) return;
    const light = body ? (LIGHT[body.kind] ?? 0.6) : 0;
    const lightX = body ? wrapX(body.x - camera.x * FAR_PARALLAX, ground.worldSize * FAR_PARALLAX) + view.width / 2 : view.width / 2;
    const horizonPx = (VIEW_SIZE / 2 - camera.y) * k;
    drawTexture(ctx, ground, camera, k, colors, lightX, light);
    if (light > 0) drawPool(ctx, body, pal, horizonPx, lightX, k, light);
    for (const m of ground.mounds) {
      // Chaque bosse défile à la vitesse de sa bande et boucle sur sa période (worldSize × parallax).
      const period = ground.worldSize * m.parallax;
      const vx = wrapX(m.x - camera.x * m.parallax, period) + view.width / 2;
      const vy = m.y - camera.y + VIEW_SIZE / 2;
      if (vx + m.w / 2 < 0 || vx - m.w / 2 > view.width || vy + m.h < 0 || vy - m.h > VIEW_SIZE) continue;
      drawMound(ctx, m, vx, vy, k, colors, lightX, light);
    }
    for (const c of ground.props) {
      const period = ground.worldSize * c.parallax;
      const vx = wrapX(c.x - camera.x * c.parallax, period) + view.width / 2;
      const vy = c.y - camera.y + VIEW_SIZE / 2;
      if (vx + c.w * 1.4 < 0 || vx - c.w * 1.4 > view.width || vy + c.w * 0.3 < 0 || vy - c.w * 0.5 > VIEW_SIZE) continue;
      PROP_DRAWERS[c.kind]?.(ctx, c, vx, vy, k, colors, t, lightX, light);
    }
  }

  return { paint };
}
